import { SQLFilters } from '../../../../api/types';
import { RegularMetricId } from '../../../../api/types-graphql';
import {
  ChartGridConfig,
  LanguageString,
  PageConfig,
  StoryboardComponentDependencies,
} from '../../../../common/components/dashboards/types';
import { Languages } from '../../../../constants';
import { getLastMonthEndDate } from '../../../../date/date-utils';
import { DataTypes, EmployeeDataFields } from '../../../../types';
import { StoryboardDateUtils } from '../../utils';
import { getMiddleChartGridConfig, gridCols, gridRows } from '../chart-layouts';
import {
  getByDimensionChartConfig,
  getNikkiTTMFactbox,
  getTopChartConfig,
} from '../common-chart-helpers';

export const getNikkiTurnoverDbPageConfig = (pageOption: {
  pageId: number;
  dashboardId: string;
  title: LanguageString;
  filters: SQLFilters;
}): PageConfig => {
  const { pageId, dashboardId, title, filters } = pageOption;
  const firstChartDimension =
    pageId === 1
      ? {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
        }
      : {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.ORGANIZATION_LEVEL_2,
        };
  const numBottomCharts = pageId === 2 ? 6 : 5;
  const bottomChartRowConfig = {
    gridRowStart: 12,
    gridRowEnd: 17,
  };
  const bottomChartGridConfigs: ChartGridConfig[] =
    numBottomCharts === 5
      ? [
          { gridColStart: 1, gridColEnd: 15, ...bottomChartRowConfig },
          { gridColStart: 15, gridColEnd: 26, ...bottomChartRowConfig },
          { gridColStart: 26, gridColEnd: 40, ...bottomChartRowConfig },
          { gridColStart: 40, gridColEnd: 54, ...bottomChartRowConfig },
          { gridColStart: 54, gridColEnd: 79, ...bottomChartRowConfig },
        ]
      : [
          { gridColStart: 1, gridColEnd: 12, ...bottomChartRowConfig },
          { gridColStart: 12, gridColEnd: 20, ...bottomChartRowConfig },
          { gridColStart: 20, gridColEnd: 32, ...bottomChartRowConfig },
          { gridColStart: 32, gridColEnd: 47, ...bottomChartRowConfig },
          { gridColStart: 47, gridColEnd: 60, ...bottomChartRowConfig },
          { gridColStart: 60, gridColEnd: 79, ...bottomChartRowConfig },
        ];
  const byDimensionChartConfigs = [
    {
      dimension: firstChartDimension,
      title: {
        [Languages.EN]: 'Organization',
        [Languages.JA]: 'Organization',
      },
      gridConfig: getMiddleChartGridConfig(1),
    },
    {
      dimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.FUNCTION_LEVEL_1,
      },
      title: {
        [Languages.EN]: 'Function',
        [Languages.JA]: 'Function',
      },
      gridConfig: getMiddleChartGridConfig(2),
    },
    {
      dimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.TENURE_GROUP,
      },
      title: {
        [Languages.EN]: 'Tenure Group',
        [Languages.JA]: 'Tenure Group',
      },
      gridConfig: bottomChartGridConfigs[0],
    },
    {
      dimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.GENDER,
      },
      title: {
        [Languages.EN]: 'Gender',
        [Languages.JA]: 'Gender',
      },
      gridConfig: bottomChartGridConfigs[1],
    },
    {
      dimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.NATIONALITY_HIERARCHICAL_LEVEL_1,
      },
      title: {
        [Languages.EN]: 'Nationality',
        [Languages.JA]: 'Nationality',
      },
      gridConfig: bottomChartGridConfigs[2],
    },
    {
      dimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.AGE_GROUP,
      },
      title: {
        [Languages.EN]: 'Age Group',
        [Languages.JA]: 'Age Group',
      },
      gridConfig: bottomChartGridConfigs[3],
    },
    {
      dimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.CUSTOM_FIELD_3_LEVEL_1,
      },
      title: {
        [Languages.EN]: 'Global Grade',
        [Languages.JA]: 'Global Grade',
      },
      gridConfig: bottomChartGridConfigs[4],
    },
    ...(pageId === 2
      ? [
          {
            dimension: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.JOB_GRADE_LEVEL_1,
            },
            title: {
              [Languages.EN]: 'Job Grade',
              [Languages.JA]: 'Job Grade',
            },
            gridConfig: bottomChartGridConfigs[5],
          },
        ]
      : []),
  ];

  const pageComponents = (storyboardDateUtils: StoryboardDateUtils) => [
    getTopChartConfig(
      storyboardDateUtils,
      filters,
      RegularMetricId.Pm0318_2CustomAttritionNikkiMonthly,
      {
        [Languages.EN]: 'Turnover % Historical',
        [Languages.JA]: 'Turnover % Historical',
      }
    ),
    getNikkiTTMFactbox(storyboardDateUtils, filters),
    ...byDimensionChartConfigs.map((chartConfig) =>
      getByDimensionChartConfig(
        storyboardDateUtils,
        chartConfig.dimension,
        RegularMetricId.Pm0318_2CustomAttritionNikkiMonthly,
        chartConfig.title,
        filters,
        chartConfig.gridConfig
      )
    ),
  ];

  const pageConfig: PageConfig = {
    pageId: pageId.toString(),
    dashboardId,
    pageNumber: pageId,
    enabled: true,
    title,
    pageOptions: {
      displayOptions: {
        enabled: true,
      },
      timeOptions: {
        timeSliderEnabled: false,
        referenceDate: getLastMonthEndDate(),
      },
      filterTray: {
        enabled: false,
      },
      dataView: {
        enabled: false,
      },
      segmentationLevel1: {
        enabled: false,
      },
      segmentationLevel2: {
        enabled: false,
      },
      benchmark: {
        enabled: false,
      },
      forecast: {
        enabled: false,
      },
    },
    gridCols: gridCols,
    gridRows: gridRows,
    configType: 'componentConfigs',
    components: ({ storyboardDateUtils }: StoryboardComponentDependencies) => [
      ...pageComponents(storyboardDateUtils),
    ],
  };
  return pageConfig;
};

const page1Filters = ``;
const page2Filters = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_YOC'`;
const page3Filters = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_JPHIL'`;
const page4Filters = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_JIND'`;
const page5Filters = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_JGCAP'`;
const page6Filters = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_JIC'`;

export const getNikkiTurnoverDbPageConfigs = (
  dashboardId: string
): PageConfig[] => {
  const pageOptions = [
    {
      pageId: 1,
      filters: page1Filters,
      title: {
        [Languages.EN]: 'YOC&ALL OC Turnover %',
        [Languages.JA]: 'YOC&ALL OC Turnover %',
      },
    },
    {
      pageId: 2,
      filters: page2Filters,
      title: {
        [Languages.EN]: 'YOC Turnover %',
        [Languages.JA]: 'YOC Turnover %',
      },
    },
    {
      pageId: 3,
      filters: page3Filters,
      title: {
        [Languages.EN]: 'JPHIL Turnover %',
        [Languages.JA]: 'JPHIL Turnover %',
      },
    },
    {
      pageId: 4,
      filters: page4Filters,
      title: {
        [Languages.EN]: 'JIND Turnover %',
        [Languages.JA]: 'JIND Turnover %',
      },
    },
    {
      pageId: 5,
      filters: page5Filters,
      title: {
        [Languages.EN]: 'JGCAP Turnover %',
        [Languages.JA]: 'JGCAP Turnover %',
      },
    },
    {
      pageId: 6,
      filters: page6Filters,
      title: {
        [Languages.EN]: 'JIC Turnover %',
        [Languages.JA]: 'JIC Turnover %',
      },
    },
  ];
  const pageConfigs = pageOptions.map((pageOption) =>
    getNikkiTurnoverDbPageConfig({
      ...pageOption,
      dashboardId,
    })
  );
  return pageConfigs;
};
